/**
 * Display formatting. Read-only: nothing here is ever sent back to the API.
 *
 * TIME RULE (CONTRACT §2, UI rule 9): the API resolves times for us. An
 * Occurrence already carries `scheduledLocalTime` as a wall-clock "HH:MM" in
 * the profile's zone. Render that. These helpers exist for the cases where you
 * genuinely only have an instant (a `takenAt`, a `createdAt`), and they convert
 * with `Intl` — the same IANA database the server's expander uses. There is no
 * date library here and no offset arithmetic anywhere.
 */

import type { DoseUnit, PlainDate } from '@medtrack/shared';
import { formatDecimalForDisplay, compareDecimal } from './decimal';

const WALL_CLOCK_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const PLAIN_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/* ------------------------------------------------------------------ *
 * Intl formatter cache. Constructing a DateTimeFormat is not free and
 * a long list re-renders often.
 * ------------------------------------------------------------------ */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  const cached = formatterCache.get(key);
  if (cached) return cached;
  let made: Intl.DateTimeFormat;
  try {
    made = new Intl.DateTimeFormat(locale, options);
  } catch {
    // An invalid IANA name should degrade, not crash a medication list.
    const { timeZone: _ignored, ...rest } = options;
    made = new Intl.DateTimeFormat(locale, rest);
  }
  formatterCache.set(key, made);
  return made;
}

function partsOf(
  fmt: Intl.DateTimeFormat,
  date: Date,
): Partial<Record<Intl.DateTimeFormatPartTypes, string>> {
  const out: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {};
  for (const part of fmt.formatToParts(date)) {
    if (part.type === 'literal') continue;
    out[part.type] = part.value;
  }
  return out;
}

function parseInstant(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A plain date as a UTC instant at midnight, for day arithmetic only. */
function plainDateToUtc(dateStr: string): Date | null {
  const m = PLAIN_DATE_RE.exec(dateStr);
  if (!m) return null;
  const [, y, mo, d] = m;
  if (!y || !mo || !d) return null;
  const ms = Date.UTC(Number(y), Number(mo) - 1, Number(d));
  return Number.isNaN(ms) ? null : new Date(ms);
}

/* ------------------------------------------------------------------ *
 * Public: dates and times
 * ------------------------------------------------------------------ */

/**
 * Today's calendar date in a profile's zone, as "YYYY-MM-DD".
 * Use this for the `from`/`to` of an occurrence query — never
 * `new Date().toISOString().slice(0, 10)`, which is today in UTC and is a day
 * out for anyone west of Greenwich after their evening dose.
 */
export function todayInZone(timezone: string, now: Date = new Date()): PlainDate {
  const p = partsOf(
    formatter('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }),
    now,
  );
  const year = p.year ?? '1970';
  const month = p.month ?? '01';
  const day = p.day ?? '01';
  return `${year}-${month}-${day}`;
}

/** Shift a plain date by whole days. Calendar-safe; no DST involvement. */
export function addDaysToPlainDate(dateStr: PlainDate, days: number): PlainDate {
  const base = plainDateToUtc(dateStr);
  if (!base) return dateStr;
  const shifted = new Date(base.getTime() + days * 86_400_000);
  const y = shifted.getUTCFullYear().toString().padStart(4, '0');
  const m = (shifted.getUTCMonth() + 1).toString().padStart(2, '0');
  const d = shifted.getUTCDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Whole days from `a` to `b`. Positive when `b` is later. */
export function daysBetweenPlainDates(a: PlainDate, b: PlainDate): number | null {
  const da = plainDateToUtc(a);
  const db = plainDateToUtc(b);
  if (!da || !db) return null;
  return Math.round((db.getTime() - da.getTime()) / 86_400_000);
}

/** The plain date an instant falls on, in the given zone. */
export function plainDateOfInstant(iso: string, timezone: string): PlainDate | null {
  const date = parseInstant(iso);
  if (!date) return null;
  return todayInZone(timezone, date);
}

/**
 * "8:00 AM".
 *
 * Accepts either an instant (`"2026-09-14T12:00:00.000Z"`) which is converted
 * into `timezone`, or a wall-clock `"HH:MM"` string, which is formatted as-is
 * with no conversion. The second form is the common one: `scheduledLocalTime`
 * on an Occurrence is already resolved and must not be re-derived.
 */
export function formatLocalTime(value: string | null | undefined, timezone: string): string {
  if (!value) return '';

  if (WALL_CLOCK_RE.test(value)) {
    // Anchor the wall clock to a fixed UTC date and format in UTC, so no zone
    // conversion can touch it.
    const [hh, mm] = value.split(':');
    const anchor = new Date(Date.UTC(2000, 0, 1, Number(hh), Number(mm)));
    return formatter('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'UTC',
    }).format(anchor);
  }

  const date = parseInstant(value);
  if (!date) return '';
  return formatter('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  }).format(date);
}

/** "Mon 14 Sep" for an instant, in the profile's zone. */
export function formatLocalDate(value: string | null | undefined, timezone: string): string {
  if (!value) return '';
  const date = parseInstant(value);
  if (!date) return '';
  const p = partsOf(
    formatter('en-US', {
      timeZone: timezone,
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }),
    date,
  );
  const thisYear = partsOf(
    formatter('en-US', { timeZone: timezone, year: 'numeric' }),
    new Date(),
  ).year;
  const tail = p.year && p.year !== thisYear ? ` ${p.year}` : '';
  return `${p.weekday ?? ''} ${p.day ?? ''} ${p.month ?? ''}${tail}`.trim();
}

/** "Mon 14 Sep, 8:00 AM" for an instant, in the profile's zone. */
export function formatLocalDateTime(value: string | null | undefined, timezone: string): string {
  if (!value) return '';
  const day = formatLocalDate(value, timezone);
  const time = formatLocalTime(value, timezone);
  if (!day) return time;
  if (!time) return day;
  return `${day}, ${time}`;
}

/**
 * "Today" | "Tomorrow" | "Yesterday" | "Mon 14 Sep".
 *
 * `dateStr` is a plain calendar date in the profile's zone; `timezone` is used
 * only to work out which day "today" is for that profile, which is the whole
 * point — a caregiver in London looking at a profile in Los Angeles must see
 * the profile's today, not their own.
 *
 * A date in a different year gets the year appended ("Mon 14 Sep 2025"), so a
 * history view scrolled back far enough is never ambiguous.
 */
export function formatRelativeDay(
  dateStr: PlainDate | null | undefined,
  timezone: string,
  now: Date = new Date(),
): string {
  if (!dateStr) return '';
  const date = plainDateToUtc(dateStr);
  if (!date) return dateStr;

  const today = todayInZone(timezone, now);
  const delta = daysBetweenPlainDates(today, dateStr);
  if (delta === 0) return 'Today';
  if (delta === 1) return 'Tomorrow';
  if (delta === -1) return 'Yesterday';

  const p = partsOf(
    formatter('en-US', {
      timeZone: 'UTC',
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }),
    date,
  );
  const todayYear = today.slice(0, 4);
  const tail = p.year && p.year !== todayYear ? ` ${p.year}` : '';
  return `${p.weekday ?? ''} ${p.day ?? ''} ${p.month ?? ''}${tail}`.trim();
}

/** "Sep 14 – Sep 20" style range label for a chart header or filter chip. */
export function formatDateRange(from: PlainDate, to: PlainDate, timezone: string): string {
  if (from === to) return formatRelativeDay(from, timezone);
  const one = plainDateToUtc(from);
  const two = plainDateToUtc(to);
  if (!one || !two) return `${from} – ${to}`;
  const fmt = formatter('en-US', { timeZone: 'UTC', day: 'numeric', month: 'short' });
  const a = partsOf(fmt, one);
  const b = partsOf(fmt, two);
  return `${a.day ?? ''} ${a.month ?? ''} – ${b.day ?? ''} ${b.month ?? ''}`;
}

/* ------------------------------------------------------------------ *
 * Public: doses
 * ------------------------------------------------------------------ */

/**
 * Units that are a count of physical things and therefore pluralise.
 * Everything else is a measurement — "2 mg", never "2 mgs".
 */
const COUNTABLE_UNITS: ReadonlySet<DoseUnit> = new Set<DoseUnit>([
  'tablet',
  'capsule',
  'puff',
  'drop',
  'patch',
  'unit',
  'application',
]);

/** Irregular plurals, should any countable unit ever need one. */
const IRREGULAR_PLURALS: Partial<Record<DoseUnit, string>> = {};

export function isCountableUnit(unit: DoseUnit): boolean {
  return COUNTABLE_UNITS.has(unit);
}

/**
 * Pluralise a dose unit against a decimal *string* amount.
 *
 * Rules, matching UI_KIT: "1 tablet", "2 tablets", "0.5 tablet".
 * Singular for any amount greater than zero and not greater than one; plural
 * otherwise (including zero: "0 tablets"). Measurement units never change.
 * Comparison is exact decimal, never `Number(amount)`.
 */
export function pluralizeUnit(unit: DoseUnit, amount: string): string {
  if (!COUNTABLE_UNITS.has(unit)) return unit;

  const aboveZero = compareDecimal(amount, '0');
  const aboveOne = compareDecimal(amount, '1');
  // Unparseable amount: fall back to singular rather than inventing an "s".
  if (aboveZero === null || aboveOne === null) return unit;

  const singular = aboveZero === 1 && aboveOne !== 1;
  if (singular) return unit;
  return IRREGULAR_PLURALS[unit] ?? `${unit}s`;
}

/**
 * "2 tablets", "0.5 tablet", "12.5 mg".
 * The amount keeps its exact decimal value, minus pointless trailing zeros.
 */
export function formatDoseAmount(amount: string, unit: DoseUnit): string {
  return `${formatDecimalForDisplay(amount)} ${pluralizeUnit(unit, amount)}`;
}

/** "500 mg" from a medication's strength, or "" when it has none. */
export function formatStrength(
  value: string | null | undefined,
  unit: string | null | undefined,
): string {
  if (!value || !unit) return '';
  const label = unit === 'mg_per_mL' ? 'mg/mL' : unit === 'percent' ? '%' : unit;
  const spacer = label === '%' ? '' : ' ';
  return `${formatDecimalForDisplay(value)}${spacer}${label}`;
}

/** "94%" or "No data" — never "0%" for an empty denominator. */
export function formatPercent(pct: number | null | undefined): string {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return 'No data';
  return `${Math.round(pct)}%`;
}

/** Initials for an avatar fallback: "Frank Chen" -> "FC", "Frank" -> "FR". */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0] ?? '';
  if (words.length === 1) return first.slice(0, 2).toUpperCase();
  const last = words[words.length - 1] ?? '';
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

/** Normalise a profile/medication colour to a CSS hex. The API stores it bare. */
export function toCssHex(colorHex: string | null | undefined, fallback = '#6366F1'): string {
  if (!colorHex) return fallback;
  const trimmed = colorHex.trim();
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return /^#[0-9a-fA-F]{6}$/.test(withHash) ? withHash : fallback;
}

/**
 * Black or white, whichever stays readable on `colorHex`.
 * Relative luminance per WCAG; the 0.55 cut is tuned for the mid-tone palette
 * profiles actually get assigned.
 */
export function readableInkOn(colorHex: string): '#000000' | '#FFFFFF' {
  const hex = toCssHex(colorHex).slice(1);
  const channel = (offset: number): number => {
    const v = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  return luminance > 0.45 ? '#000000' : '#FFFFFF';
}
